import React, { useCallback, useContext, useEffect, useState } from 'react'
import { dustinAuth } from '@dustin-web/auth-js'
import {
  UserData,
  AuthConfig,
  SignInOptions,
  SignOutOptions,
  Contact,
  GetAuthDataOptions,
  AuthData as AuthDataType,
} from '@dustin-web/auth-js'

type AuthData = {
  isLoading: boolean
  isAuthenticated: boolean
  userData: UserData | null
  signIn: (options?: SignInOptions) => void
  signOut: (options?: SignOutOptions) => void
  getAccessToken: () => Promise<string | null> | undefined
  getZendeskToken: () => Promise<string | null> | undefined
  setCurrentContact: (contactId: number) => Promise<boolean>
  getContacts: () => Promise<Contact[] | null> | undefined
  getAuthData: (code: string, options?: GetAuthDataOptions | undefined) => Promise<AuthDataType | null>
}

export type { AuthConfig }

const context = React.createContext<AuthData | null>(null)

export const useAuth = () => {
  const data = useContext(context)
  if (!data) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return data
}

type Props = {
  config: AuthConfig
  initialAuthenticated?: boolean
  children: React.ReactNode
}

export const AuthProvider = ({ config, initialAuthenticated, children, ...props }: Props) => {
  const [authClient] = useState(dustinAuth(config))

  const [isLoading, setIsLoading] = useState(initialAuthenticated === undefined)
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuthenticated ?? false)
  const [userData, setUserData] = useState<UserData | null>(null)

  useEffect(() => {
    const process = async () => {
      const authenticated = await authClient.isAuthenticated()
      setIsAuthenticated(authenticated ?? false)
      setIsLoading(false)
    }
    process()
  }, [])

  useEffect(() => {
    const userDataFromAuth = isAuthenticated ? authClient.getUserData() : null
    setUserData(userDataFromAuth)
  }, [isAuthenticated, authClient])

  const handleLogin = useCallback((options?: SignInOptions) => {
    authClient.signIn(options)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogout = useCallback((options?: SignOutOptions) => {
    authClient.signOut(options)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetAccessToken = useCallback(() => {
    return authClient.getAccessToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetZendeskToken = useCallback(() => {
    return authClient.getZendeskToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setCurrentContact = useCallback((contactId: number) => {
    return authClient.setCurrentContact(contactId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetContacts = useCallback(() => {
    return authClient.getContacts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetAuthData = useCallback((code: string, options?: GetAuthDataOptions) => {
    return authClient.getAuthData(code, options)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const authData = {
    isLoading,
    isAuthenticated,
    userData,
    signIn: handleLogin,
    signOut: isAuthenticated ? handleLogout : () => null,
    getAccessToken: isAuthenticated ? handleGetAccessToken : () => new Promise<string>(() => null),
    getZendeskToken: isAuthenticated
      ? handleGetZendeskToken
      : () => new Promise<string>(() => null),
    setCurrentContact: isAuthenticated ? setCurrentContact : () => new Promise<boolean>(() => null),
    getContacts: isAuthenticated ? handleGetContacts : () => new Promise<Contact[]>(() => []),
    getAuthData: handleGetAuthData,
  }

  return (
    <context.Provider value={authData} {...props}>
      {children}
    </context.Provider>
  )
}

export default {
  AuthProvider,
  useAuth,
}
